import React, { useEffect, useRef, useState } from "react";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";

const SAMPLES = [
  { label: "Makeup", file: "makeup.csv" },
  { label: "Fragrances", file: "fragrances.csv" },
  { label: "Care", file: "care.csv" },
  { label: "Devices", file: "devices.csv" },
];

const COST_COLUMNS = ["cost", "profit", "profit_margin"];
const REGULAR_PRICE_COLUMNS = ["regular_price"];
const SALE_PRICE_COLUMNS = ["sale_price"];

// Minimal CSV parser/serializer that preserves quoted fields containing
// commas/newlines. We need this to drop cost columns for non-permission
// users without mangling the multiline description fields in the samples.
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
};

const serializeCsv = (rows) =>
  rows
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? "");
          return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(",")
    )
    .join("\n");

// Flatten any newlines/blank lines inside a cell to a single space. The sample
// descriptions are stored as multi-paragraph text, which Excel renders as very
// tall, mostly-empty rows — collapsing keeps every record on one compact line.
const normalizeCell = (cell) =>
  String(cell ?? "")
    .replace(/\s*[\r\n]+\s*/g, " ")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

// Drop columns the user lacks permission to submit and normalize whitespace so
// the template opens cleanly in Excel.
const cleanCsv = (text, dropSet) => {
  const rows = parseCsv(text);
  if (rows.length === 0) return text;
  const header = rows[0];
  const keepIdx =
    dropSet.size === 0
      ? header.map((_, i) => i)
      : header
          .map((h, i) => (dropSet.has(h.trim()) ? -1 : i))
          .filter((i) => i >= 0);
  return serializeCsv(
    rows.map((r) => keepIdx.map((i) => normalizeCell(r[i]))),
  );
};

const ExcelDownloadButton = () => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const { hasAction } = usePermissions();
  // The CSV template is for *uploading* products (POST /bulk-stage). Each
  // price field is rejected without EDIT on its scope, so we strip those
  // columns for users without permission — sparing them ERR_FORBIDDEN_FIELD
  // on every row.
  const canSubmitCost = hasAction(MENU.PRODUCT_COSTS, PERMISSION.EDIT);
  const canSubmitRegularPrice = hasAction(
    MENU.PRODUCT_REGULAR_PRICE,
    PERMISSION.EDIT,
  );
  const canSubmitSalePrice = hasAction(
    MENU.PRODUCT_SALE_PRICE,
    PERMISSION.EDIT,
  );
  const stripSet = new Set();
  if (!canSubmitCost) COST_COLUMNS.forEach((c) => stripSet.add(c));
  if (!canSubmitRegularPrice)
    REGULAR_PRICE_COLUMNS.forEach((c) => stripSet.add(c));
  if (!canSubmitSalePrice)
    SALE_PRICE_COLUMNS.forEach((c) => stripSet.add(c));

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleDownload = async (file) => {
    try {
      const res = await fetch(`${process.env.PUBLIC_URL}/samples/${file}`);
      let text = await res.text();
      // Drop any pre-existing BOM so it isn't doubled and so the header
      // parser sees clean column names.
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
      const content = cleanCsv(text, stripSet);
      // Prepend a UTF-8 BOM so Excel decodes the Arabic columns as UTF-8
      // instead of falling back to Windows-1252 (which garbles the text).
      const blob = new Blob(["\uFEFF" + content], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download sample:", e);
    }
    setOpen(false);
  };

  return (
    <div className={`dropdown ${open ? "show" : ""}`} ref={wrapperRef}>
      <button
        type="button"
        className="btn btn-primary dropdown-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Download Sample
      </button>
      <ul
        className={`dropdown-menu dropdown-menu-end ${open ? "show" : ""}`}
      >
        {SAMPLES.map(({ label, file }) => (
          <li key={file}>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => handleDownload(file)}
            >
              {label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ExcelDownloadButton;
